/**
 * Pull a human-readable message out of an Axios error.
 *
 * FastAPI returns 422 validation errors as `detail: Array<{type, loc, msg,
 * input, ctx}>` — passing that array straight to React renders crashes the
 * page with error #31 ("Objects are not valid as a React child"). For 4xx
 * we typically get `detail: string`. For other errors (network, parse) we
 * fall back to the Error's own message or a static fallback.
 *
 * All onError handlers should funnel through this so future 422-returning
 * endpoints can't repro the same crash.
 */

interface PydanticErrorItem {
  type?: string
  loc?: Array<string | number>
  msg?: string
  input?: unknown
  ctx?: Record<string, unknown>
}

interface AxiosLikeError {
  response?: {
    status?: number
    data?: { detail?: unknown; request_id?: unknown; retry_after?: unknown }
    headers?: Record<string, unknown>
  }
  message?: string
}

/**
 * The request id the backend attached to this response, if any.
 *
 * RequestIdMiddleware puts it on EVERY response as `X-Request-ID`, and CORS
 * already exposes it — the only missing link was a reader. It also lands in the
 * body as `request_id`, but only on the middleware's own 500 envelope; a 500
 * raised explicitly (`raise HTTPException(500, "Sign-in failed…")`, of which
 * there are several) carries the header and nothing else. Prefer the body, fall
 * back to the header, so both shapes work.
 */
function requestIdOf(e: AxiosLikeError): string | null {
  const fromBody = e?.response?.data?.request_id
  if (typeof fromBody === 'string' && fromBody) return fromBody
  const headers = e?.response?.headers
  // Axios lower-cases response header names; tolerate the other spelling in
  // case something ever hands us a raw Headers-like object.
  const fromHeader = headers?.['x-request-id'] ?? headers?.['X-Request-ID']
  return typeof fromHeader === 'string' && fromHeader ? fromHeader : null
}

function formatPydanticItem(item: PydanticErrorItem): string | null {
  if (!item || typeof item.msg !== 'string') return null
  // `.loc` starts with "body" / "query" / "path". "body" is dropped — the form
  // in front of the user is the default place to look, and "body.title" makes
  // every ordinary field error uglier. The OTHER prefixes are kept: a rejected
  // query or path parameter used to render identically to a form field, and
  // the user went and "fixed" the form.
  const [where, ...rest] = item.loc ?? []
  const path = rest.join('.')
  const field = where === 'body' || where === undefined ? path : [where, path].filter(Boolean).join(' ')
  return field ? `${field}: ${item.msg}` : item.msg
}

/** Axios's own message for any non-2xx. Carries no information the caller
 *  does not already have, and hides the fallback they wrote. */
const AXIOS_STATUS_BOILERPLATE = /^Request failed with status code \d+$/

export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  const e = err as AxiosLikeError
  const status = e?.response?.status
  const detail = e?.response?.data?.detail

  // Rate limiting, before the generic `detail` branch — because that branch was
  // showing the limiter's own string. slowapi's handler puts `str(exc.detail)`
  // in `detail`, which is the raw rule: a user who tried to sign in six times
  // in a minute was told "5 per 1 minute". True, and not a sentence.
  if (status === 429) {
    const seconds = Number(e?.response?.data?.retry_after)
    return Number.isFinite(seconds) && seconds > 0
      ? `Too many attempts. Try again in ${Math.ceil(seconds)} seconds.`
      : 'Too many attempts. Please wait a moment and try again.'
  }

  if (typeof detail === 'string') return withRequestId(detail, e, status)

  // Structured 4xx: the backend returns `{code, message, ...}` for rules that
  // need to be machine-identifiable as well as readable (B4's caretaker block,
  // stage warnings). Without this branch a dict detail falls all the way
  // through to `e.message`, which for an Axios error is the useless "Request
  // failed with status code 400" — that is issue #977, and it sits exactly
  // where B4 now fires.
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const message = (detail as { message?: unknown }).message
    if (typeof message === 'string' && message) return withRequestId(message, e, status)
  }

  if (Array.isArray(detail)) {
    const lines = detail
      .map((d) => formatPydanticItem(d as PydanticErrorItem))
      .filter((s): s is string => !!s)
    if (lines.length) return lines.join('; ')
  }

  // An AxiosError IS an Error, and its `.message` is ALWAYS the boilerplate
  // "Request failed with status code N". This branch sat above the fallback, so
  // it always won — and roughly forty call sites pass a fallback that had never
  // once been shown to anyone. #977 chased this exact symptom (four transition
  // sites showing "Request failed with status code 400" instead of the reason)
  // and fixed the dict branch above; the ordering that actually produces it
  // stayed, so the symptom kept coming back somewhere else.
  //
  // Only the boilerplate is worthless. A message from a request that never got
  // a response — "Network Error", "timeout of 10000ms exceeded" — tells the
  // reader something the caller's fallback cannot, so it still wins.
  if (typeof e?.message === 'string' && e.message && !AXIOS_STATUS_BOILERPLATE.test(e.message)) {
    return withRequestId(e.message, e, status)
  }
  // Reaching the fallback with a response means the backend refused and said
  // nothing — a bug worth a reference even on a 4xx.
  return withRequestId(fallback, e, status, e?.response !== undefined)
}

/**
 * Append `(ref: <id>)` when the failure is one the reader cannot act on.
 *
 * Only for 5xx. A 400 saying "Promoted requires a real priority bucket" is
 * something they fix themselves; a reference number there is noise on every
 * validation message in the app. A 500 is the opposite — "it broke" with
 * nothing to quote is why support conversations start with "it says an error".
 *
 * Skipped when the id is already in the text: the middleware's own 500 envelope
 * says "Quote request id …" in `detail`, and repeating it would read as two
 * different numbers.
 *
 * One 4xx exception: a 4xx that carried NO detail at all. That is a bug — the
 * backend refused without saying why — not a rule the user broke, so it is the
 * one case on the 4xx side where a support reference is the only useful thing
 * the message can carry. `bareClientError` marks it from the fallback path.
 */
function withRequestId(
  message: string,
  e: AxiosLikeError,
  status?: number,
  bareClientError = false,
): string {
  if (status === undefined || (status < 500 && !bareClientError)) return message
  const id = requestIdOf(e)
  if (!id || message.includes(id)) return message
  return `${message} (ref: ${id})`
}

/**
 * Same normalisation, for responses read as a Blob.
 *
 * The four export call sites use `responseType: 'blob'`, which applies to the
 * ERROR body too: axios hands back a Blob containing the JSON, `err.response
 * .data.detail` is undefined, and the reader gets a generic "Export failed"
 * while the server's actual explanation — and its request id — sit unread
 * inside the Blob.
 */
export async function blobErrorMessage(
  err: unknown,
  fallback = 'Something went wrong',
): Promise<string> {
  const e = err as AxiosLikeError & { response?: { data?: unknown } }
  const data = e?.response?.data as { text?: () => Promise<string> } | undefined

  if (data && typeof data.text === 'function') {
    try {
      const parsed = JSON.parse(await data.text())
      return apiErrorMessage({ ...e, response: { ...e.response, data: parsed } }, fallback)
    } catch {
      // Not JSON — a truncated download, an HTML error page from the ingress.
      // Fall through rather than surface a parse error the reader cannot use.
    }
  }
  return apiErrorMessage(err, fallback)
}
